import { BillingEntity, OnboardingStatus } from './billingEntity.model';
import { useBillingEntitiesQuery } from './billingEntity.slice';
import { useIsBillingStripeEnabled } from './billingLine.hooks';

/** Statuses whose Stripe dashboard is worth opening: onboarded, possibly with pending items. */
const STRIPE_DASHBOARD_STATUSES: readonly OnboardingStatus[] = [
  'ACTIVE',
  'PAST_DUE'
];

export const canOpenStripeDashboard = (entity: BillingEntity): boolean =>
  entity.currentDoctorMember &&
  STRIPE_DASHBOARD_STATUSES.includes(entity.onboardingStatus);

/**
 * The entity whose Stripe dashboard the calling doctor may open, or `undefined` while the list
 * loads, on a hospital without Stripe, or when the doctor is not a member of an onboarded entity.
 * A doctor belongs to at most one entity per hospital, so the first match is the only one.
 */
export const useStripeDashboardEntity = (): BillingEntity | undefined => {
  const stripeEnabled = useIsBillingStripeEnabled();
  const { currentData } = useBillingEntitiesQuery(undefined, {
    skip: !stripeEnabled
  });
  return currentData?.find(canOpenStripeDashboard);
};

/**
 * Statuses the hosted onboarding can still move forward. `SUSPENDED` is excluded on purpose:
 * Stripe has paused the account, and re-running the form does not lift that.
 */
const ONBOARDING_STATUSES: readonly OnboardingStatus[] = [
  'NOT_STARTED',
  'PENDING',
  'PAST_DUE'
];

/** Whether the hosted onboarding form is still the way forward for this entity. */
export const canResumeOnboarding = (entity: BillingEntity): boolean =>
  ONBOARDING_STATUSES.includes(entity.onboardingStatus);

/** Statuses meaning payouts are paused and somebody has to act, one way or another. */
const ATTENTION_STATUSES: readonly OnboardingStatus[] = [
  'PAST_DUE',
  'SUSPENDED'
];

/**
 * The entity of the hospital whose Stripe account most needs attention — payouts are paused
 * either way, whether or not the onboarding form can still lift it (see
 * {@link canResumeOnboarding}) — or `undefined` while the list loads, on a hospital without
 * Stripe, or when every entity is healthy. Ranked by severity, not by list position: SUSPENDED
 * wins over PAST_DUE whichever entity comes first, since the two carry different messages and
 * `canResumeOnboarding` withholds the onboarding anchor for SUSPENDED alone — a PAST_DUE entity
 * ordered ahead of a SUSPENDED one must never hide the worse state from the admin. Follows
 * {@link useStripeDashboardEntity}: a plain read of the entity list already fetched for the
 * section, no polling of its own.
 */
export const useStripeEntityAttention = (): BillingEntity | undefined => {
  const stripeEnabled = useIsBillingStripeEnabled();
  const { currentData } = useBillingEntitiesQuery(undefined, {
    skip: !stripeEnabled
  });
  return (
    currentData?.find((entity) => entity.onboardingStatus === 'SUSPENDED') ??
    currentData?.find((entity) =>
      ATTENTION_STATUSES.includes(entity.onboardingStatus)
    )
  );
};
